import { useEffect, useState } from "react";

/**
 * Full-screen brand splash shown for ~750 ms on first paint so users get a
 * smooth "app is opening" feel instead of a flash of blank white while React
 * hydrates. Also listens for the `ff:updating` window event dispatched by
 * `lib/versionCheck.js` when a new server build is detected — showing the
 * same splash with an "Updating…" caption so the reload feels intentional.
 */
export default function SplashScreen() {
  const [state, setState] = useState({ show: true, updating: false });

  useEffect(() => {
    const t = setTimeout(() => setState({ show: false, updating: false }), 750);
    const onUpdate = () => setState({ show: true, updating: true });
    window.addEventListener("ff:updating", onUpdate);
    return () => {
      clearTimeout(t);
      window.removeEventListener("ff:updating", onUpdate);
    };
  }, []);

  if (!state.show) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center"
      style={{ background: "linear-gradient(180deg,#3B5A3E 0%,#2A4230 100%)" }}
      data-testid="splash-screen"
    >
      <div className="relative flex items-center justify-center">
        <div
          className="absolute rounded-full border-4 border-white/25 border-t-white"
          style={{ width: 160, height: 160, animation: "ff-splash-spin 1.2s linear infinite" }}
        />
        <img
          src="/logo.png"
          alt="Shop, Cal & Meal"
          className="rounded-full"
          style={{
            width: 112, height: 112, objectFit: "cover",
            boxShadow: "0 12px 40px rgba(0,0,0,0.35)",
            animation: "ff-splash-pulse 2s ease-in-out infinite",
          }}
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      </div>
      <div className="mt-10 text-white/95 text-xl font-medium" style={{ fontFamily: "Outfit, sans-serif" }}>
        Shop, Cal &amp; Meal
      </div>
      <div className="mt-2 text-xs text-white/70 tracking-wide uppercase" data-testid="splash-status">
        {state.updating ? "Updating to the latest version…" : "Getting ready…"}
      </div>
      <style>{`
        @keyframes ff-splash-spin { to { transform: rotate(360deg); } }
        @keyframes ff-splash-pulse {
          0%,100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
      `}</style>
    </div>
  );
}
